import React, { useContext, useRef, useLayoutEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Box,
  Badge,
  Image,
  HStack,
  Text,
  Flex,
  IconButton,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";
import { IoChatbubble } from "react-icons/io5";
import { HamburgerIcon, SunIcon, MoonIcon } from "@chakra-ui/icons";
import "../styles/cart.css";
import icon from "../assets/icon.png";
import { AuthContext } from "../store/auth-context";
import { useMessages } from "../store/messages-context";
import { useLanguageModal } from "../context/LanguageModalContext";
import LanguageMenuTrigger from "./LanguageMenuTrigger";
import { APP_CONTENT_SHELL } from "../util/contentShell";

const Navigation = () => {
  const { t } = useTranslation();
  const { openLanguageModal } = useLanguageModal();
  const { colorMode, setColorMode } = useColorMode();
  const authCtx = useContext(AuthContext);
  const { totalUnreadCount } = useMessages();
  const location = useLocation();
  const isLanding = location.pathname === "/";
  const isAdminRoute = location.pathname.startsWith("/admin");
  const hasUser = typeof window !== "undefined" && localStorage.getItem("user");
  const guestLandingNav = isLanding && !hasUser;

  const headerMeasureRef = useRef(null);
  const [headerBlockHeight, setHeaderBlockHeight] = useState(148);
  const spacerHeight = headerBlockHeight + 12;
  // On mobile, authenticated app routes use the dedicated MobileTopBar instead of this floating-pill nav.
  const isMobileAppShell =
    !!hasUser && !isLanding && location.pathname !== "/Account-disabled";
  const scrollScrimBase = useColorModeValue("rgb(248, 250, 252)", "rgb(10, 10, 11)");
  const scrollScrimMid = useColorModeValue("rgba(248, 250, 252, 0.72)", "rgba(10, 10, 11, 0.55)");

  const navBarBg = useColorModeValue("rgb(255, 255, 255)", "rgb(18, 18, 20)");
  const navBarBorder = useColorModeValue("rgba(15, 23, 42, 0.08)", "rgba(255, 255, 255, 0.14)");
  const navBarStripShadow = useColorModeValue(
    "0 1px 0 rgba(15, 23, 42, 0.06)",
    "0 1px 0 rgba(255, 255, 255, 0.06)"
  );
  const logoTextColor = useColorModeValue("gray.900", "white");
  const badgeBg = useColorModeValue("brand.50", "whiteAlpha.100");
  const badgeColor = useColorModeValue("brand.800", "brand.300");
  const badgeBorder = useColorModeValue("brand.100", "whiteAlpha.200");
  const devBadgeBg = useColorModeValue("red.100", "red.900");
  const devBadgeColor = useColorModeValue("red.800", "red.200");
  const msgBtnBg = useColorModeValue("white", "white");
  const msgBtnColor = useColorModeValue("gray.900", "surface.900");
  const msgBtnShadow = useColorModeValue(
    "md",
    "0 8px 24px rgba(52, 211, 153, 0.3), 0 12px 32px rgba(253, 224, 71, 0.12)"
  );
  const msgBtnBorder = useColorModeValue("gray.200", "transparent");
  const msgBtnOuterShadow = useColorModeValue("sm", "none");
  const msgBtnHoverBg = useColorModeValue("gray.50", "whiteAlpha.900");
  const menuBorder = useColorModeValue("gray.300", "whiteAlpha.300");
  const menuColor = useColorModeValue("gray.800", "white");
  const menuHoverBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const getStartedBg = useColorModeValue("gray.900", "white");
  const getStartedColor = useColorModeValue("white", "surface.900");
  const themeRailBg = useColorModeValue("gray.100", "rgba(0,0,0,0.45)");
  const themeRailBorder = useColorModeValue("gray.200", "whiteAlpha.150");
  const themeInactiveColor = useColorModeValue("gray.500", "whiteAlpha.500");
  const themeActiveShadow = useColorModeValue(
    "0 2px 8px rgba(15,23,42,0.12), inset 0 1px 0 rgba(255,255,255,0.9)",
    "0 2px 12px rgba(52,211,153,0.22), 0 0 18px rgba(253,224,71,0.14), inset 0 1px 0 rgba(255,255,255,0.12)"
  );
  const darkMoonGradient = "linear(to-br, brand.500, brand.600)";

  /** Match `App.js` main content shell so nav aligns with page content. */
  const navContentShell = APP_CONTENT_SHELL;

  useLayoutEffect(() => {
    const node = headerMeasureRef.current;
    if (!node || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const update = () => {
      const h = node.getBoundingClientRect().height;
      setHeaderBlockHeight(Math.ceil(h));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(node);
    return () => ro.disconnect();
  }, [hasUser, authCtx.isAccountActive, isLanding, location.pathname, colorMode]);

  const logoInner = (
    <Flex align="center" justify="flex-start" gap={3} minH="40px">
      <Box
        position="relative"
        borderRadius="xl"
        overflow="hidden"
        flexShrink={0}
        lineHeight={0}
        display="flex"
        alignItems="center"
      >
        <Image
          borderRadius="xl"
          h="40px"
          w="40px"
          objectFit="cover"
          src={icon}
          alt=""
          display="block"
        />
      </Box>
      <HStack
        spacing={2}
        align="center"
        flexWrap="wrap"
        minH="40px"
        display={{ base: "none", lg: "flex" }}
      >
        <Text
          as="span"
          fontSize={{ base: "lg", sm: "xl" }}
          fontWeight="800"
          color={logoTextColor}
          letterSpacing="tight"
          lineHeight="shorter"
          display="inline-flex"
          alignItems="center"
        >
          {t("nav.payinapo")}
        </Text>
        <HStack spacing={1} align="center" alignSelf="center">
          <Badge
            fontSize="0.55em"
            bg={badgeBg}
            color={badgeColor}
            px="1.5"
            py="0.5"
            borderRadius="full"
            border="1px solid"
            borderColor={badgeBorder}
          >
            {process.env.REACT_APP_Version}
          </Badge>
          {process.env.REACT_APP_NAME === "DEV" && (
            <Badge fontSize="0.55em" bg={devBadgeBg} color={devBadgeColor} px="1.5" py="0.5" borderRadius="full">
              DEV
            </Badge>
          )}
        </HStack>
      </HStack>
    </Flex>
  );

  let loginActions = null;
  if (localStorage.getItem("user") && authCtx.isAccountActive) {
    loginActions = (
      <HStack spacing={{ base: 2, md: 3 }} align="center">
        <Link to="/Messages">
          <Box position="relative" display="inline-flex" overflow="visible" zIndex={1}>
            <Button
              aria-label={
                totalUnreadCount > 0
                  ? t("nav.messagesUnread", { count: totalUnreadCount })
                  : t("nav.messages")
              }
              size="sm"
              color={msgBtnColor}
              leftIcon={<IoChatbubble />}
              bg={msgBtnBg}
              borderWidth="1px"
              borderColor={msgBtnBorder}
              boxShadow={msgBtnOuterShadow}
              px={{ base: 2, sm: 3 }}
              _hover={{
                bg: msgBtnHoverBg,
                transform: "translateY(-1px)",
                boxShadow: msgBtnShadow,
              }}
              _active={{ transform: "translateY(0)" }}
              transition="all 0.2s"
            >
              <Text as="span" display={{ base: "none", sm: "inline" }}>
                {t("nav.messages")}
              </Text>
            </Button>
            {totalUnreadCount > 0 && (
              <Flex
                position="absolute"
                top="-4px"
                right="-4px"
                minW="20px"
                h="20px"
                px="6px"
                align="center"
                justify="center"
                borderRadius="full"
                bg="rgba(254, 226, 226, 0.95)"
                color="#b91c1c"
                fontSize="11px"
                fontWeight="bold"
                lineHeight={1}
                borderWidth="1px"
                borderColor="rgba(252, 165, 165, 0.7)"
                boxShadow="0 4px 12px rgba(239, 68, 68, 0.25)"
                pointerEvents="none"
                aria-hidden="true"
                zIndex={2}
              >
                {totalUnreadCount > 99 ? "99+" : totalUnreadCount}
              </Flex>
            )}
          </Box>
        </Link>
        <Link to="/settings">
          <Button
            size="sm"
            variant="outline"
            borderColor={menuBorder}
            color={menuColor}
            _hover={{
              bg: menuHoverBg,
              borderColor: "brand.400",
              color: "brand.500",
            }}
            transition="all 0.2s"
          >
            <HamburgerIcon />
          </Button>
        </Link>
      </HStack>
    );
  }

  const lightBtnBg = useColorModeValue("white", "whiteAlpha.900");

  const appearanceToggle = (
    <Flex
      align="center"
      p="3px"
      gap={0}
      borderRadius="full"
      bg={themeRailBg}
      borderWidth="1px"
      borderColor={themeRailBorder}
      boxShadow="inset 0 1px 3px rgba(0,0,0,0.08)"
      role="radiogroup"
      aria-label={t("common.appearance")}
    >
      <IconButton
        type="button"
        aria-label={t("nav.useLightMode")}
        aria-pressed={colorMode === "light"}
        icon={<SunIcon w={4} h={4} />}
        size="sm"
        h="34px"
        minW="34px"
        px={3}
        borderRadius="full"
        variant="unstyled"
        color={colorMode === "light" ? "gray.900" : themeInactiveColor}
        bg={colorMode === "light" ? lightBtnBg : "transparent"}
        boxShadow={colorMode === "light" ? themeActiveShadow : "none"}
        _hover={{
          color: colorMode === "light" ? "gray.900" : "whiteAlpha.800",
          bg: colorMode === "light" ? undefined : "whiteAlpha.50",
        }}
        onClick={() => setColorMode("light")}
      />
      <IconButton
        type="button"
        aria-label={t("nav.useDarkMode")}
        aria-pressed={colorMode === "dark"}
        icon={<MoonIcon w={4} h={4} />}
        size="sm"
        h="34px"
        minW="34px"
        px={3}
        borderRadius="full"
        variant="unstyled"
        color={colorMode === "dark" ? "white" : themeInactiveColor}
        bg={colorMode === "dark" ? undefined : "transparent"}
        bgGradient={colorMode === "dark" ? darkMoonGradient : undefined}
        boxShadow={colorMode === "dark" ? themeActiveShadow : "none"}
        _hover={{
          color: "white",
          bg: colorMode === "dark" ? undefined : "whiteAlpha.80",
        }}
        onClick={() => setColorMode("dark")}
      />
    </Flex>
  );

  if (isAdminRoute) return null;

  return (
    <>
      <Box
        ref={headerMeasureRef}
        position="fixed"
        top={0}
        left={0}
        right={0}
        zIndex={1000}
        pt={0}
        px={0}
        pointerEvents="none"
        sx={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
        display={isMobileAppShell ? { base: "none", lg: "block" } : "block"}
      >
        <Box w="100%" pointerEvents="auto">
          <Box
            w="100%"
            bg={navBarBg}
            borderWidth="0 0 1px 0"
            borderStyle="solid"
            borderColor={navBarBorder}
            boxShadow={navBarStripShadow}
          >
            <Box
              {...navContentShell}
              py={{ base: 2.5, md: 3 }}
              display="flex"
              alignItems="center"
            >
              <Flex w="100%" align="center">
              {guestLandingNav ? (
                <Flex w="100%" align="center" justify="space-between" gap={4} minH="44px">
                  <Flex align="center" justify="center" minH="44px" flexShrink={0}>
                    <Link to="/" display="inline-flex" alignItems="center">
                      {logoInner}
                    </Link>
                  </Flex>
                  <HStack spacing={3} align="center" flexShrink={0}>
                    <LanguageMenuTrigger
                      onClick={openLanguageModal}
                      display={{ base: "none", sm: "flex" }}
                    />
                    <Button
                      as="a"
                      href="#auth"
                      size="sm"
                      display={{ base: "none", sm: "flex" }}
                      alignItems="center"
                      bg={getStartedBg}
                      color={getStartedColor}
                      rightIcon={
                        <Text as="span" fontSize="lg" lineHeight={1}>
                          →
                        </Text>
                      }
                      _hover={{
                        opacity: 0.92,
                        boxShadow:
                          "0 8px 28px rgba(52, 211, 153, 0.28), 0 14px 36px rgba(253, 224, 71, 0.18)",
                      }}
                    >
                      {t("nav.getStarted")}
                    </Button>
                    {appearanceToggle}
                  </HStack>
                </Flex>
              ) : (
                <Flex justify="space-between" align="center" gap={4} wrap="wrap" w="100%" minH="44px">
                  <Flex align="center" justify="center" flexShrink={0} minH="44px">
                    {hasUser ? (
                      <Link to="/shop" display="inline-flex" alignItems="center">
                        {logoInner}
                      </Link>
                    ) : (
                      <Link to="/" display="inline-flex" alignItems="center">
                        {logoInner}
                      </Link>
                    )}
                  </Flex>
                  <HStack spacing={3} align="center" flexShrink={0} ml="auto">
                    <Box display={{ base: "none", lg: "block" }}>
                      <LanguageMenuTrigger onClick={openLanguageModal} />
                    </Box>
                    <Box display={{ base: "none", lg: "block" }}>{loginActions}</Box>
                    {!hasUser && appearanceToggle}
                  </HStack>
                </Flex>
              )}
              </Flex>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        display={isMobileAppShell ? { base: "none", lg: "block" } : "block"}
        position="fixed"
        top={`${headerBlockHeight}px`}
        left={0}
        right={0}
        h="64px"
        zIndex={999}
        pointerEvents="none"
        bg={`linear-gradient(to bottom, ${scrollScrimBase} 0%, ${scrollScrimMid} 55%, transparent 100%)`}
        aria-hidden
      />
      <Box
        h={isMobileAppShell ? { base: 0, lg: `${spacerHeight}px` } : `${spacerHeight}px`}
        minH={isMobileAppShell ? { base: 0, lg: "120px" } : "120px"}
        flexShrink={0}
        aria-hidden
      />
    </>
  );
};

export default Navigation;
